<script lang="ts" setup>
import { useAsyncData } from '#app'

/* Utils */
import { checkResultApiRequest } from '@/utils/checkResultApiRequest'

/* Stores */
import { useAppStore } from '@/store/app/app'
import { useCityStore } from '@/store/city/city'
import { useDeliveryStore } from '@/store/delivery/delivery'
import { useMapSettingsStore } from '@/store/map-settings/map-settings'
import { useToggleStore } from '@/store/toggle/toggle'

/* Components */
import DeliverySearch from '@/components/Delivery/DeliverySearch.vue'
import DeliveryPointFilters from '@/components/Delivery/DeliveryPointFilters.vue'
import DeliverySectionControls from '@/components/Delivery/DeliverySectionControls.vue'
import DeliveryPointsSection from '@/components/Delivery/DeliveryPointsSection.vue'
import DeliveryInfo from '@/components/Delivery/DeliveryInfo.vue'

const { isMobileOrTablet } = useDevice()
const {
  cityInfo: { padeg, domain },
} = useCityStore()

const deliveryStore = useDeliveryStore()
const appStore = useAppStore()
const mapSettingsStore = useMapSettingsStore()
const { newFetchingApiEnabled } = useToggleStore()

const { $apiService } = useNuxtApp()

const { data, error } = await useAsyncData(async (nuxtApp) => {
  const fetchPage = nuxtApp?.$apiService.delivery.fetchPage
  const fetchMapSettings = nuxtApp?.$apiService.city.fetchMapSettings

  if (!fetchPage || !fetchMapSettings) return { page: null, settings: null }

  const promiseAll = await Promise.all([fetchPage(), fetchMapSettings()])

  const page = checkResultApiRequest(promiseAll[0])
  const settings = checkResultApiRequest(promiseAll[1])

  return { page, settings }
})

if (error.value) showError(error.value)

if (data.value) {
  deliveryStore.setPage(data.value.page)
  mapSettingsStore.setMapSettings(data.value.settings)
}

const currentView = ref(0)

const isWebview = computed(() => appStore.isWebview)

const cafeEnabled = computed(() => deliveryStore.cafePoints.length > 0)

const filteredTags = computed(() => deliveryStore.filteredTags)

const pageTitle = computed(() =>
  cafeEnabled.value ? 'Доставка и кафе' : deliveryStore.page.title,
)

const breadcrumbs = computed(() => {
  return [
    { text: `Доставка суши в ${padeg?.pred}`, href: '/' },
    { text: pageTitle.value || '', href: '/delivery/' },
  ]
})

const setCurrentView = (value: number) => (currentView.value = value)

const filteredPoints = computed(() => deliveryStore.filteredPoints)

const showPoints = computed(() => Boolean(filteredPoints.value.length))

// Новый fetch для карты
const fetchNewAreas = async () => {
  const resultNewAreas = await $apiService.delivery.fetchAreas()

  if (resultNewAreas) deliveryStore.setAreasData(resultNewAreas.data)
}

// Старый fetch для карты, в две ручки
const fetchOldAreas = async () => {
  const resultOldAreas = await $apiService.delivery.fetchOldAreas()
  const resultOldPoints = await $apiService.delivery.fetchOldPointsOfAreas()

  deliveryStore.setAreasData({
    areas: resultOldAreas,
    points: resultOldPoints,
  })
}

onMounted(() => {
  newFetchingApiEnabled ? fetchNewAreas() : fetchOldAreas()
})
</script>

<template>
  <div class="delivery" itemscope itemtype="http://schema.org/Organization">
    <app-head
      :title="deliveryStore.page.seo.title"
      :seo="deliveryStore.page.seo"
    />

    <the-breadcrumbs
      v-if="!isWebview"
      class="delivery__breadcrumbs"
      :breadcrumbs="breadcrumbs"
      :domain="domain"
    />

    <h1 v-if="!isWebview" class="delivery__title">
      {{ pageTitle }}
    </h1>

    <p class="delivery__description">
      Введите свой адрес, и мы расскажем об условиях доставки, покажем ближайшие
      рестораны и точки самовывоза
    </p>

    <delivery-search class="delivery__search" />

    <delivery-point-filters
      v-if="isMobileOrTablet"
      :filtered-tags="filteredTags"
      :show-points="showPoints"
    />

    <div v-if="isMobileOrTablet" class="delivery__tabs">
      <delivery-section-controls @update:model-value="setCurrentView" />
    </div>

    <delivery-points-section
      class="delivery__points"
      :current-view="currentView"
    />

    <delivery-info />
  </div>
</template>

<style lang="scss" scoped>
.delivery {
  padding: 40px calc(50% - 600px) 40px;

  @include media('<desktop-mini') {
    padding: 20px 20px 40px;
  }

  &__breadcrumbs {
    margin-bottom: 20px;
  }

  &__title {
    margin-bottom: 50px;
    font-size: 28px;
    font-weight: 800;
    line-height: 36px;

    @include media('<desktop-mini') {
      margin-bottom: 20px;
      font-size: 24px;
      line-height: 28px;
    }

    @include media('<tablet') {
      font-size: 18px;
      line-height: 21px;
    }
  }

  &__description {
    margin-bottom: 25px;
    font-size: 16px;
    font-weight: 500;
    line-height: 19px;

    @include media('<desktop-mini') {
      font-size: 12px;
    }
  }

  &__points {
    margin-bottom: 80px;

    @include media('<desktop-mini') {
      margin-bottom: 40px;
    }
  }

  &__search {
    margin-bottom: 18px;
  }

  &__tabs {
    margin-bottom: 30px;
  }
}
</style>
